import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL } from '../config/api';
import {
  Drop, Ruler, Calculator, ArrowRight, Lightbulb, Warning, CheckCircle,
} from '@phosphor-icons/react';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { RelatedTools } from '../components/RelatedTools';

/**
 * Standalone catch-cup test calculator. Single-purpose utility — no login,
 * no signup, just a tool anyone can use to measure sprinkler precipitation
 * rate and understand what their number means. Feeds into /water-budget for
 * the full irrigation plan.
 */

const CatchCupPage = () => {
  const [mode, setMode] = useState('single'); // 'single' | 'multi'
  const [ml, setMl] = useState(50);
  const [minutes, setMinutes] = useState(10);
  const [diameter, setDiameter] = useState(3);
  const [cups, setCups] = useState(['50', '48', '52', '45', '50']);
  const [result, setResult] = useState(null);
  const [uniformityResult, setUniformityResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Catch-Cup Sprinkler Audit — TurfPulse';
  }, []);

  const measure = async () => {
    setError(null);
    setResult(null);
    setUniformityResult(null);
    setLoading(true);
    try {
      if (mode === 'single') {
        const r = await axios.post(
          `${API_URL}/api/water-budget/catch-cup?volume_ml=${ml}&run_time_minutes=${minutes}&cup_diameter_in=${diameter}`,
        );
        setResult(r.data);
      } else {
        const numeric = cups
          .map((v) => parseFloat(v))
          .filter((v) => !Number.isNaN(v) && v >= 0);
        if (numeric.length < 2) {
          setError('Enter at least 2 cup measurements for uniformity analysis.');
          setLoading(false);
          return;
        }
        const r = await axios.post(`${API_URL}/api/water-budget/uniformity`, {
          volumes_ml: numeric,
          run_time_minutes: minutes,
          cup_diameter_in: diameter,
        });
        setUniformityResult(r.data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Check your inputs and try again.');
    } finally {
      setLoading(false);
    }
  };

  const addCup = () => setCups((cs) => [...cs, '']);
  const removeCup = (i) => setCups((cs) => cs.filter((_, idx) => idx !== i));
  const updateCup = (i, v) => setCups((cs) => cs.map((c, idx) => (idx === i ? v : c)));

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="catch-cup-page">
      <SEO
        title="Catch-Cup Calculator — measure your sprinkler precipitation rate"
        description="Free tool to measure your actual sprinkler output using the catch-cup test. Calculates precipitation rate and distribution uniformity — the two numbers every irrigation plan hinges on."
        keywords="catch cup test, sprinkler precipitation rate, distribution uniformity, irrigation audit, lawn watering test, sprinkler calibration"
        path="/catch-cup"
      />
      <BreadcrumbJsonLd items={[
        { label: 'Home', path: '/' },
        { label: 'Catch-Cup Calculator', path: '/catch-cup' },
      ]} />
      {/* Hero */}
      <section className="border-b border-zinc-800/80">
        <div className="max-w-3xl mx-auto px-5 md:px-8 pt-16 pb-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-emerald-500 mb-4">
            <Ruler size={14} weight="fill" />
            <span>Catch-Cup Sprinkler Audit</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="cc-page-title">
            How much water are your sprinklers actually putting down?
          </h1>
          <p className="mt-5 text-zinc-400 text-base max-w-2xl leading-relaxed">
            The tuna can test is the industry-standard way pros audit sprinklers.
            Takes 15 minutes, costs you nothing, and gives you a number that
            actually matches what's hitting your lawn — no manufacturer marketing,
            no guessing.
          </p>
        </div>
      </section>

      {/* How-to */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-10 border-b border-zinc-800/80">
        <h2 className="text-zinc-300 text-xs uppercase tracking-[0.2em] mb-6">How to run the test</h2>
        <ol className="space-y-5 text-sm text-zinc-300 leading-relaxed">
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-semibold">1</span>
            <div>
              <div className="text-zinc-100 font-medium">Grab 3–5 tuna cans</div>
              <div className="text-zinc-500 text-sm mt-1">
                They're ~3" diameter and straight-sided — perfect for this. Flat-bottom cat food cans or
                straight-sided coffee mugs work too. <strong>Do not</strong> use cups with sloped sides (measurement error).
              </div>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-semibold">2</span>
            <div>
              <div className="text-zinc-100 font-medium">Place them across ONE zone</div>
              <div className="text-zinc-500 text-sm mt-1">
                Spread them out — one near a head, one in the middle, one at the far edge. Measures
                both output AND uniformity (a missing or clogged head will show up as an empty cup).
              </div>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-semibold">3</span>
            <div>
              <div className="text-zinc-100 font-medium">Run the zone for 10 minutes</div>
              <div className="text-zinc-500 text-sm mt-1">
                Standard test length. Don't run longer — if your zone puddles at 10 min, you've also
                just learned about your soil infiltration rate (that's a separate problem to fix).
              </div>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-semibold">4</span>
            <div>
              <div className="text-zinc-100 font-medium">Measure the volume in each cup</div>
              <div className="text-zinc-500 text-sm mt-1">
                Kitchen measuring cup with ml markings works. Write down each number, then average them.
                <strong className="text-zinc-300"> Enter the average</strong> in the calculator below.
              </div>
            </div>
          </li>
        </ol>
      </section>

      {/* Calculator */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-10 border-b border-zinc-800/80">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h2 className="text-zinc-300 text-xs uppercase tracking-[0.2em]">Calculator</h2>
          <div className="flex gap-1 p-0.5 bg-zinc-900 border border-zinc-800 rounded-lg">
            <button
              onClick={() => setMode('single')}
              className={`px-3 py-1.5 text-xs rounded transition-colors ${mode === 'single' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
              data-testid="cc-mode-single"
            >
              Single cup (PR)
            </button>
            <button
              onClick={() => setMode('multi')}
              className={`px-3 py-1.5 text-xs rounded transition-colors ${mode === 'multi' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
              data-testid="cc-mode-multi"
            >
              Multi-cup (Uniformity)
            </button>
          </div>
        </div>

        {mode === 'single' ? (
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="flex flex-col gap-2">
              <span className="text-zinc-400 text-xs uppercase tracking-wider">Avg. volume in cup</span>
              <div className="relative">
                <input
                  type="number" value={ml} onChange={(e) => setMl(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 outline-none rounded-lg text-zinc-100 font-mono text-lg"
                  data-testid="cc-page-ml"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm">ml</span>
              </div>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-zinc-400 text-xs uppercase tracking-wider">Run time</span>
              <div className="relative">
                <input
                  type="number" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 outline-none rounded-lg text-zinc-100 font-mono text-lg"
                  data-testid="cc-page-min"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm">min</span>
              </div>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-zinc-400 text-xs uppercase tracking-wider">Cup diameter</span>
              <div className="relative">
                <input
                  type="number" step="0.1" value={diameter} onChange={(e) => setDiameter(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 focus:border-emerald-500 outline-none rounded-lg text-zinc-100 font-mono text-lg"
                  data-testid="cc-page-diameter"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm">in</span>
              </div>
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-2">
                <span className="text-zinc-400 text-xs uppercase tracking-wider">Run time</span>
                <div className="relative">
                  <input
                    type="number" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-100 font-mono outline-none focus:border-emerald-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm">min</span>
                </div>
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-zinc-400 text-xs uppercase tracking-wider">Cup diameter</span>
                <div className="relative">
                  <input
                    type="number" step="0.1" value={diameter} onChange={(e) => setDiameter(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-100 font-mono outline-none focus:border-emerald-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm">in</span>
                </div>
              </label>
            </div>

            <div>
              <div className="text-zinc-400 text-xs uppercase tracking-wider mb-2">
                Volume in each cup (ml)
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {cups.map((c, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <input
                      type="number"
                      value={c}
                      onChange={(e) => updateCup(i, e.target.value)}
                      placeholder={`Cup ${i + 1}`}
                      className="flex-1 min-w-0 px-2 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 font-mono text-sm outline-none focus:border-emerald-500"
                      data-testid={`cc-cup-${i}`}
                    />
                    {cups.length > 2 && (
                      <button
                        onClick={() => removeCup(i)}
                        className="px-1.5 text-zinc-600 hover:text-rose-400 text-sm"
                        aria-label="Remove cup"
                      >×</button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={addCup}
                className="mt-3 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-xs text-zinc-300"
                data-testid="cc-add-cup"
              >
                + Add cup
              </button>
            </div>
          </div>
        )}

        <button
          onClick={measure}
          disabled={loading}
          className="mt-6 w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-medium rounded-lg transition-colors"
          data-testid="cc-page-measure"
        >
          <Calculator size={18} weight="bold" />
          {mode === 'single' ? 'Calculate precipitation rate' : 'Calculate uniformity'}
        </button>

        {error && (
          <div className="mt-5 p-4 bg-rose-500/10 border border-rose-500/30 rounded text-rose-300 text-sm" data-testid="cc-page-error">
            {error}
          </div>
        )}

        {result && (
          <div className="mt-8 p-6 border border-emerald-500/40 bg-emerald-500/5 rounded-xl" data-testid="cc-page-result">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 mb-2 flex items-center gap-2">
              <CheckCircle size={14} weight="fill"/> Your zone's precipitation rate
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-5xl font-semibold text-emerald-300 font-mono">{result.precipitation_rate_in_hr}</span>
              <span className="text-zinc-400 text-xl">in/hr</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded">
                <div className="text-zinc-500 text-xs uppercase tracking-wider">Depth in 10 min</div>
                <div className="text-zinc-100 font-mono text-lg">{result.measured_depth_in}"</div>
              </div>
              <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded">
                <div className="text-zinc-500 text-xs uppercase tracking-wider">Interpretation</div>
                <div className="text-zinc-300 text-sm">{result.interpretation}</div>
              </div>
            </div>

            <Link
              to="/water-budget"
              className="mt-5 inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium"
              data-testid="cc-page-wb-link"
            >
              Plug this into your water budget <ArrowRight size={14} weight="bold"/>
            </Link>
          </div>
        )}

        {uniformityResult && (
          <div className="mt-8 p-6 border border-emerald-500/40 bg-emerald-500/5 rounded-xl space-y-4" data-testid="cc-du-result">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 flex items-center gap-2">
              <CheckCircle size={14} weight="fill"/> Distribution Uniformity Report
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded">
                <div className="text-zinc-500 text-xs uppercase tracking-wider">Average PR</div>
                <div className="text-zinc-100 font-mono text-xl">{uniformityResult.average_pr_in_hr} in/hr</div>
              </div>
              <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded">
                <div className="text-zinc-500 text-xs uppercase tracking-wider">DU (Lower Q)</div>
                <div className={`font-mono text-xl ${
                  uniformityResult.distribution_uniformity_lq >= 0.75 ? 'text-emerald-300' :
                  uniformityResult.distribution_uniformity_lq >= 0.60 ? 'text-amber-300' : 'text-rose-300'
                }`}>
                  {Math.round(uniformityResult.distribution_uniformity_lq * 100)}%
                </div>
                <div className="text-[10px] text-zinc-600 mt-0.5">Target ≥75%</div>
              </div>
              <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded">
                <div className="text-zinc-500 text-xs uppercase tracking-wider">CU (Christiansen)</div>
                <div className="text-zinc-100 font-mono text-xl">
                  {Math.round(uniformityResult.coefficient_of_uniformity * 100)}%
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              uniformityResult.verdict === 'excellent' || uniformityResult.verdict === 'acceptable'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                : uniformityResult.verdict === 'poor'
                ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                : 'border-rose-500/30 bg-rose-500/10 text-rose-200'
            }`}>
              <div className="text-xs uppercase tracking-wider font-medium opacity-80 mb-1">
                Verdict: {uniformityResult.verdict}
              </div>
              <div className="text-sm">{uniformityResult.verdict_message}</div>
            </div>

            <div>
              <div className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Per-cup breakdown</div>
              <div className="space-y-1.5">
                {uniformityResult.cups.map((c) => (
                  <div
                    key={c.cup_index}
                    className={`flex items-center justify-between p-2.5 rounded border ${
                      c.flag === 'under' ? 'bg-rose-500/10 border-rose-500/30' :
                      c.flag === 'over' ? 'bg-amber-500/10 border-amber-500/30' :
                      'bg-zinc-900/60 border-zinc-800'
                    }`}
                    data-testid={`cc-du-cup-${c.cup_index}`}
                  >
                    <div className="text-sm">
                      <span className="font-mono text-zinc-300">Cup {c.cup_index}</span>
                      <span className="text-zinc-500 ml-2 font-mono text-xs">{c.volume_ml}ml · {c.rate_in_hr} in/hr ({c.pct_of_avg}%)</span>
                    </div>
                    <span className={`text-xs uppercase tracking-wider font-medium ${
                      c.flag === 'under' ? 'text-rose-300' :
                      c.flag === 'over' ? 'text-amber-300' :
                      'text-emerald-300'
                    }`}>
                      {c.flag}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Link
              to="/water-budget"
              className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium"
            >
              Plug average PR into your water budget <ArrowRight size={14} weight="bold"/>
            </Link>
          </div>
        )}
      </section>

      {/* Reference table */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-10 border-b border-zinc-800/80">
        <h2 className="text-zinc-300 text-xs uppercase tracking-[0.2em] mb-6">What's a normal number?</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-wider">
              <th className="text-left py-2 font-normal">Head type</th>
              <th className="text-right py-2 font-normal">Typical PR (in/hr)</th>
              <th className="text-left py-2 pl-4 font-normal">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            <tr><td className="py-2.5 text-zinc-300">Fixed spray</td><td className="text-right text-zinc-400 font-mono">1.2 – 1.8</td><td className="pl-4 text-zinc-500 text-xs">High output. Runoff-prone on clay.</td></tr>
            <tr><td className="py-2.5 text-zinc-300">Rotor</td><td className="text-right text-zinc-400 font-mono">0.4 – 0.6</td><td className="pl-4 text-zinc-500 text-xs">Medium output. Good for medium/large zones.</td></tr>
            <tr><td className="py-2.5 text-zinc-300">MP Rotator</td><td className="text-right text-zinc-400 font-mono">0.3 – 0.5</td><td className="pl-4 text-zinc-500 text-xs">Best uniformity. Slow and gentle.</td></tr>
            <tr><td className="py-2.5 text-zinc-300">Drip line</td><td className="text-right text-zinc-400 font-mono">0.3 – 0.6</td><td className="pl-4 text-zinc-500 text-xs">Spot-watering. Not comparable to overhead.</td></tr>
            <tr><td className="py-2.5 text-zinc-300">Bubbler</td><td className="text-right text-zinc-400 font-mono">0.8 – 1.2</td><td className="pl-4 text-zinc-500 text-xs">Direct-to-root. Trees, shrubs only.</td></tr>
          </tbody>
        </table>
        <div className="mt-4 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg flex items-start gap-3">
          <Warning size={18} className="text-amber-400 flex-shrink-0 mt-0.5"/>
          <div className="text-amber-200 text-sm">
            <strong>If your measured PR is far outside these ranges:</strong> your sprinklers may be
            mis-nozzled, mis-pressured, or the heads themselves are failing. That's where a 30-minute
            DIY audit pays for itself.
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-zinc-500 text-xs uppercase tracking-[0.2em] mb-2">Next step</div>
            <div className="text-zinc-200 text-lg">
              Convert your PR into an actual watering plan — minutes per zone, split into cycles.
            </div>
          </div>
          <Link
            to="/water-budget"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors"
            data-testid="cc-page-wb-cta"
          >
            Open Water Budget <ArrowRight size={16} weight="bold"/>
          </Link>
        </div>
      </section>
      <RelatedTools currentPath="/catch-cup" />
    </div>
  );
};

export default CatchCupPage;
